/**
 * The KPI row for a range (Home's "Last 28 days" and the Overview): the vertical's headline first,
 * then plays, watch time and viewers (definitions.ts kpiTiles), each with its change against the
 * previous period and, where there is a daily series, a sparkline over the same days. Reads the
 * summary (it holds both periods' totals), the previous period's summary when its daily series is
 * wanted (the Overview's faint line) and, when the headline is a live figure, both periods' live events.
 */

import { analytics, type LiveReport, type Summary } from "./api";
import { definition, headline, kpiTiles, type Definition, type MetricId } from "./definitions";
import { changeOf, formatFigure, liveFigure, totalsFigure } from "./figures";
import type { Change } from "./format";
import { alignToDays, endsToday } from "./range";
import type { ReportScope } from "./scope";
import { useReport, type Report } from "./use-report";

export interface Kpi {
  id: MetricId;
  definition: Definition;
  value: string;
  change: Change;
  trend?: number[];
  /** The trend's last day is today, still going. */
  trendPartial?: boolean;
}

export interface Kpis {
  summary: Report<Summary>;
  /** The previous period's own summary (its daily series); null unless asked for. */
  previous: Report<Summary | null>;
  tiles: Kpi[] | null;
  /** Any answer holds sample data. */
  sample: boolean;
  /** Nobody has watched in either period (and nothing is sample): say why instead of zeros. */
  empty: boolean;
}

const DAILY: Partial<Record<MetricId, "plays" | "watchSeconds" | "viewers">> = { plays: "plays", watchSeconds: "watchSeconds", viewers: "viewers" };

export function useKpis(scope: ReportScope, options: { ids?: MetricId[]; previousSeries?: boolean } = {}): Kpis {
  const ids = options.ids ?? kpiTiles(scope.pack);
  const wantPrevious = !!options.previousSeries;
  const { client, range, previous } = scope;
  const head = headline(scope.pack);
  const needsLive = ids.includes(head.id) && head.source.from === "live";
  const summary = useReport(() => analytics.summary(client, range), [client, range]);
  const before = useReport(() => (wantPrevious ? analytics.summary(client, previous) : Promise.resolve(null)), [client, previous, wantPrevious]);
  const live = useReport<{ now: LiveReport; before: LiveReport } | null>(
    () => (needsLive ? Promise.all([analytics.live(client, range), analytics.live(client, previous)]).then(([now, b]) => ({ now, before: b })) : Promise.resolve(null)),
    [client, range, previous, needsLive],
  );

  const s = summary.data;
  const ready = !!s && (!needsLive || live.data !== null || live.failed);
  const tiles = ready ? ids.map((id): Kpi => {
    const def = definition(id, scope.pack);
    if (id === head.id && head.source.from === "live") {
      const key = head.source.key;
      const now = live.data ? liveFigure(key, live.data.now.events) : null;
      const then = live.data ? liveFigure(key, live.data.before.events) : null;
      return { id, definition: def, value: formatFigure(id, now), change: changeOf(id, now, then) };
    }
    const now = totalsFigure(id, s.totals);
    const then = totalsFigure(id, s.previousTotals);
    const daily = DAILY[id];
    return { id, definition: def, value: formatFigure(id, now), change: changeOf(id, now, then), ...(daily ? { trend: alignToDays(s.series, range, (p) => p[daily]), trendPartial: endsToday(range) } : {}) };
  }) : null;

  const sample = !!(s?.sample || before.data?.sample || live.data?.now.sample || live.data?.before.sample);
  const empty = !!s && !sample && s.totals.plays === 0 && s.previousTotals.plays === 0;
  return { summary, previous: before, tiles, sample, empty };
}
